import styled from "styled-components";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

const PanelOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: ${({ $isOpen }) => ($isOpen ? "block" : "none")};
  animation: ${({ $isOpen }) => ($isOpen ? "fadeIn 0.2s" : "none")};
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const PanelContainer = styled.div<{ $isOpen: boolean; $wide?: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  height: 100dvh;
  padding-bottom: env(safe-area-inset-bottom, 0px);
  width: ${({ $wide }) => ($wide ? "min(880px, 94vw)" : "480px")};
  max-width: ${({ $wide }) => ($wide ? "94vw" : "90vw")};
  background-color: ${colors.background};
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  transform: translateX(${({ $isOpen }) => ($isOpen ? "0" : "100%")});
  transition: transform 0.3s ease-in-out;
  overflow-y: auto;
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.x3}px;
  border-bottom: 1px solid ${colors.border};
  position: sticky;
  top: 0;
  background-color: ${colors.background};
  z-index: 10;

  @media (max-width: 768px) {
    padding: ${spacing.x1}px;
  }
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: ${fontSizes.large}px;
  font-weight: 600;
  color: ${colors.text};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: ${fontSizes.xlarge}px;
  color: ${colors.textMuted};
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover {
    background-color: ${colors.background};
    color: ${colors.text};
  }
`;

const PanelContent = styled.div`
  flex: 1;
  padding: ${spacing.x3}px;
  margin-bottom: 100px;

  @media (max-width: 768px) {
    padding: ${spacing.x1}px;
  }
`;

interface SidebarPanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  wide?: boolean;
}

export const SidebarPanel = ({
  isOpen,
  onClose,
  title,
  children,
  wide,
}: SidebarPanelProps) => {
  return (
    <>
      <PanelOverlay $isOpen={isOpen} onClick={onClose} />
      <PanelContainer $isOpen={isOpen} $wide={wide}>
        <PanelHeader>
          <PanelTitle>{title}</PanelTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </PanelHeader>
        <PanelContent>{children}</PanelContent>
      </PanelContainer>
    </>
  );
};

